import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  OnInit,
  OnChanges,
  EventEmitter, ChangeDetectorRef
} from '@angular/core';
import { Observable } from "rxjs/Observable";
import { Utils } from "../../services";
import { CISIA_URL_FOR_CONNECTION_SPEED, CS_F_VERSION } from "../../constants";
import { CISIA_URL_TO_CHECK_CONNECTION } from "../../constants";
import { AssessmentActions, AssessmentService } from "../../logics";
import { Store } from "@ngrx/store";
import { SocketHandler } from "../../services";
import { Subject } from "rxjs/Subject";

import {
  Translate
} from '../../services';

@Component({
  selector: 'cs-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="cs-modal-bg"></div>
    <div 
      class="cs-modal"
      [ngClass]="{
        'cs-modal-warning': content.isWarning,
        'cs-modal-console': content.isConsole
      }">
      <span 
        class="ion-alert-circled"
        *ngIf="content.isWarning">
      </span>
      <h1 
        *ngIf="content.title"
        [innerHtml]="content.title">
      </h1>
      <p 
        *ngIf="content.description" 
        [innerHtml]="content.description">
      </p>

      <p class="app-version" *ngIf="content.appVersion">
        {{ content.appVersion }}<br />
        {{ (translations$ | async)['modal#local_id'] }}: {{ localID }}<br />
        {{ (translations$ | async)['modal#local_ip'] }}: {{ localIP ? localIP : '-' }}
      </p>

      <ul 
        class="cs-modal-validation-list"
        *ngIf="content.validationsList">
        <li 
          *ngFor="let validation of content.validationsList; let i = index">
          <i 
		  	*ngIf="validation.key === 'server'"
            class="cs-modal-validation-list__icon"
            [ngClass]="{
              'ion-checkmark-circled': isConnectionAvaliable,
              'ion-close-circled': !isConnectionAvaliable,
              'fail': !isConnectionAvaliable
            }">
          </i>
          <i 
		  	*ngIf="validation.key !== 'server'"
            class="cs-modal-validation-list__icon"
            [ngClass]="{
              'ion-clock': validation.manualCheck,
              'ion-checkmark-circled': !validation.manualCheck && (validation.value || manualCheckResult(validation)),
              'ion-close-circled': (validation.key !== 'speedTest') && !(validation.value || manualCheckResult(validation)),
              'ion-alert-circled': !validation.manualCheck && (!(validation.value || manualCheckResult(validation))),
              'fail': !validation.manualCheck && !(validation.value || manualCheckResult(validation))
            }">
          </i>
          <span class="cs-modal-validation-list__text">
            {{ validation.type }}
          </span>
          <span *ngIf="validation.manualCheck" class="cs-modal-validation-manual-check">
            <i
                class="toggle-play"
                [ngClass]="{
                  'ion-play': !isChecking(validation),
                  'ion-stop': isChecking(validation)
                }"
                (click)="manualCheck(i)">
            </i>
          </span>
          <div *ngIf="hasMessage(validation)">{{ validationCheckingData[validation.key].message }}</div>
        </li>
      </ul>
      
      <div>
        <p *ngIf="content.checks">
          {{ translations.confirmLabel }}
        </p>
        <label 
          class="cs-form-check-label"
          (click)="labelClick($event)"
          *ngFor="let question of content.checks; let i = index">
          <input 
            type="checkbox" 
            class="cs-form-check"
            [disabled]="inputs.loading" 
            [(ngModel)]="checksStatus[i]"
            (ngModelChange)="checkboxChange()"
            [ngModelOptions]="{standalone: true}">
           {{ question }}
        </label>

        <div *ngIf="content.useCodeValidation">
          <label 
            class="cs-form-input-label">
            {{ translations.insertCodeLabel }}
          </label>
          <input 
            type="password"
            placeholder="{{ (translations$ | async)['modal#insert_code'] }}"
            [(ngModel)]="validationCode"
            (ngModelChange)="isSubmitAvailable()"            
            [ngModelOptions]="{standalone: true}">
        </div>

        <div class="cs-form-btn-group">
          <button 
            class="cs-btn cs-btn-warning"
            [ngClass]="{
                'cs-btn-loader': inputs.loading,
                'disabled': !canBeSubmitted || (!isConnectionAvaliable && !content.isConsole)
              }"
            *ngIf="content.submitText"
            (click)="submitIt()">
            {{ inputs.loading ? '' : content.submitText }}
            <div 
              class="cs-loader"
              *ngIf="inputs.loading">
                <div class="bounce1"></div>
                <div class="bounce2"></div>
                <div class="bounce3"></div>
            </div>
          </button>
          <button 
            class="cs-btn"
            *ngIf="!content.preventClose && !inputs.loading"
            (click)="cancelIt()">
            {{ (translations$ | async)['modal#cancel'] }}
          </button>
        </div>

        <div class="cs-form-btn-group cs-alert-warning" *ngIf="!isConnectionAvaliable">
          {{ translations.noConnection }}
        </div>

        <div class="cs-form-btn-group cs-alert-warning" *ngIf="syncStatus === 'ko' || syncStatus === 'pending'">
          <span *ngIf="syncStatus === 'pending'">{{ (translations$ | async)['modal#sync_status_pending'] }}</span>
          <span *ngIf="syncStatus === 'ko'">{{ (translations$ | async)['modal#sync_status_ko'] }}</span>
        </div>

      </div>
    </div>
  `
})

export class CsModalComponent implements OnInit, OnChanges {
  @Input() inputs: any = {};
  @Input() translations: any = {};
  @Output() cancelRequest = new EventEmitter<Object>();
  @Output() submitRequest = new EventEmitter<Object>();
  @Output() submitValidation = new EventEmitter<Object>();

  translations$ = this.translate.getTranslations$();

  cs_f_version: string = CS_F_VERSION;
  checksStatus: Array<boolean> = [];
  areChecksOk: boolean = false;
  canBeSubmitted: boolean = false;
  validationCode: string = '';
  validationChecking = {};
  validationCheckingData = {};
  content: any = {};
  isConnectionAvaliable: boolean = true;
  destroyed$: Subject<any> = new Subject<any>();
  localID: string;
  localIP: string | null;
  syncStatus: string;

  constructor(
    private translate: Translate,
    private store: Store<any>,
    private utils: Utils,
    private chRef: ChangeDetectorRef,
    private assessmentActions: AssessmentActions,
    private assessmentService: AssessmentService,
    private socketHandler: SocketHandler
  ) { }

  _init() {
    this.content = this.inputs.content;
    if (this.content && !this.content.checks) {
      this.areChecksOk = true;
    }

    this.syncStatus = '';
    if(this.content && this.content.modalId === 'new_section'){
      this.assessmentService.getSyncStatus$()
        .takeUntil(this.destroyed$)
        .subscribe(status => {
          this.syncStatus = status;

          // update
          this.chRef.detectChanges();
          Observable.timer(0, 100).take(1).subscribe((tick) => {
            this.chRef.detectChanges();
            this.chRef.reattach();
          });
        });
    }


    this.isSubmitAvailable();

    this.isConnectionAvaliable = this.socketHandler.isConnected;

    if (this.content.isConsole) {

    }

    // set local*
    this.localID = this.utils.getLocalID();
    this.localIP = this.utils.getLocalIP();
  }

  // NODE: crazy interaction request -.-'
  labelClick(event) {
    if (event && event.target &&
      event.target.className === 'cs-form-check-label') {
      event.preventDefault();
      return false;
    }
  }

  hasMessage(validation) {
    return typeof this.validationCheckingData[validation.key] !== 'undefined' && this.validationCheckingData[validation.key].message;
  }

  manualCheckResult(validation) {
    return typeof this.validationCheckingData[validation.key] !== 'undefined' && this.validationCheckingData[validation.key].result;
  }

  isChecking(validation) {
    return typeof this.validationCheckingData[validation.key] !== 'undefined' && this.validationCheckingData[validation.key].subscriber !== null;
  }

  manualCheck(i) {
    let validation = this.content.validationsList[i]
      , key = validation.key
    ;
    if (typeof this.validationChecking[key] === 'undefined') {
      this.validationChecking[key] = false;
    }
    switch(key) {
      case 'sendLog':
        this.checkSendLog(validation, !this.validationChecking[key]);
        break;
      case 'speedTest':
        this.checkSpeedTest(validation, !this.validationChecking[key]);
        break;
      case 'pingCisia':
        this.checkPingCisia(validation, !this.validationChecking[key]);
        break;
    }

    this.validationChecking[key] = !this.validationChecking[key];
    this.chRef.detectChanges();
    Observable.timer(0, 100).take(1).subscribe((tick) => {
      this.chRef.detectChanges();
      this.chRef.reattach();
    });

  }

  checkSendLog(validation, start) {
    let sub
      , key = validation.key
    ;
    if (!start && typeof this.validationCheckingData[key] !== "undefined") {
      sub = this.validationCheckingData[key].subscriber;
      if (typeof sub !== 'undefined' && sub !== null) {
        sub.unsubscribe();
      }
      this.validationCheckingData[key].subscriber = null;
      return;
    }

    this.store.dispatch(this.assessmentActions.sendLog());
    this.validationChecking[key] = false;
    if (typeof this.validationCheckingData[key] === 'undefined') {
      this.validationCheckingData[key] = {};
    }
    this.validationCheckingData[key]['result'] = true;
    this.validationCheckingData[key]['message'] = 'Log inviato';
    this.chRef.detectChanges();
    Observable.timer(0, 100).take(1).subscribe((tick) => {
      this.chRef.detectChanges();
      this.chRef.reattach();
    });
    return;
    /*
    sub = this.utils.get$(CISIA_URL_FOR_CONNECTION_SPEED, {reportProgress: true}).subscribe({
      next: (v) => {
        console.log(v);
      },
      error: (e) => {
        this.validationChecking[key] = !this.validationChecking[key];
        this.validationCheckingData[key]['message'] = 'Errore';
        this.validationCheckingData[key]['subscriber'] = null;
        this.validationCheckingData[key]['result'] = false;
        this.chRef.detectChanges();
        Observable.timer(0, 100).take(1).subscribe((tick) => {
          this.chRef.detectChanges();
          this.chRef.reattach();
        });
      },
      complete: () => {
        let endTime =  (new Date().getTime());
        this.validationChecking[key] = !this.validationChecking[key];
        this.validationCheckingData[key]['endTime'] = endTime;
        this.validationCheckingData[key]['message'] = 'Completato in ' + (this.validationCheckingData[key]['endTime'] - this.validationCheckingData[key]['startTime']) + ' ms';
        this.validationCheckingData[key]['subscriber'] = null;
        this.validationCheckingData[key]['result'] = true;
        this.chRef.detectChanges();
        Observable.timer(0, 100).take(1).subscribe((tick) => {
          this.chRef.detectChanges();
          this.chRef.reattach();
        });
      },
    });

    if (typeof this.validationCheckingData[validation.key] === 'undefined') {
      this.validationCheckingData[validation.key] = {};
    }
    this.validationCheckingData[validation.key]['subscriber'] = sub;
    this.validationCheckingData[validation.key]['startTime'] = startTime;
    this.validationCheckingData[validation.key]['message'] = '';
    */
  }

  checkSpeedTest(validation, start) {
    let sub
      , startTime = (new Date().getTime())
      , key = validation.key
    ;
    if (!start && typeof this.validationCheckingData[key] !== 'undefined') {
      sub = this.validationCheckingData[key].subscriber;
      if (sub !== null) {
        sub.unsubscribe();
      }
      this.validationCheckingData[key].subscriber = null;
      return;
    }

    sub = this.utils.get$(CISIA_URL_FOR_CONNECTION_SPEED, {reportProgress: true}).subscribe({
      next: (v) => {
        console.log(v);
      },
      error: (e) => {
        this.validationChecking[key] = !this.validationChecking[key];
        this.validationCheckingData[key]['message'] = 'Errore';
        this.validationCheckingData[key]['subscriber'] = null;
        this.validationCheckingData[key]['result'] = false;
        this.chRef.detectChanges();
        Observable.timer(0, 100).take(1).subscribe((tick) => {
          this.chRef.detectChanges();
          this.chRef.reattach();
        });
      },
      complete: () => {
        let endTime =  (new Date().getTime());
        this.validationChecking[key] = !this.validationChecking[key];
        this.validationCheckingData[key]['endTime'] = endTime;
        this.validationCheckingData[key]['message'] = 'Completato in ' + (this.validationCheckingData[key]['endTime'] - this.validationCheckingData[key]['startTime']) + ' ms';
        this.validationCheckingData[key]['subscriber'] = null;
        this.validationCheckingData[key]['result'] = true;
        this.chRef.detectChanges();
        Observable.timer(0, 100).take(1).subscribe((tick) => {
          this.chRef.detectChanges();
          this.chRef.reattach();
        });
      },
    });

    if (typeof this.validationCheckingData[validation.key] === 'undefined') {
      this.validationCheckingData[validation.key] = {};
    }
    this.validationCheckingData[validation.key]['subscriber'] = sub;
    this.validationCheckingData[validation.key]['startTime'] = startTime;
    this.validationCheckingData[validation.key]['message'] = '';
  }

  checkPingCisia(validation, start) {
    let sub
      , key = validation.key
    ;
    if (!start && typeof this.validationCheckingData[key] !== 'undefined') {
      sub = this.validationCheckingData[key].subscriber;
      if (sub !== null) {
        sub.unsubscribe();
      }
      this.validationCheckingData[key].subscriber = null;
      return;
    }

    sub = this.utils.get$(CISIA_URL_TO_CHECK_CONNECTION).subscribe({
      next: (v) => {
      },
      error: (e) => {
        this.validationChecking[key] = !this.validationChecking[key];
        this.validationCheckingData[key]['message'] = e;
        this.validationCheckingData[key]['subscriber'] = null;
        this.validationCheckingData[key]['result'] = false;
        this.chRef.detectChanges();
        Observable.timer(0, 100).take(1).subscribe((tick) => {
          this.chRef.detectChanges();
          this.chRef.reattach();
        });
      },
      complete: () => {
        this.validationChecking[key] = !this.validationChecking[key];
        this.validationCheckingData[key]['message'] = 'OK';
        this.validationCheckingData[key]['subscriber'] = null;
        this.validationCheckingData[key]['result'] = true;
        this.chRef.detectChanges();
        Observable.timer(0, 100).take(1).subscribe((tick) => {
          this.chRef.detectChanges();
          this.chRef.reattach();
        });
      },
    });

    if (typeof this.validationCheckingData[validation.key] === 'undefined') {
      this.validationCheckingData[validation.key] = {};
    }
    this.validationCheckingData[validation.key]['subscriber'] = sub;
    this.validationCheckingData[validation.key]['message'] = '';
  }

  cancelIt() {
    this.cancelRequest.emit();
  }

  submitIt() {
    if (this.inputs.loading || !this.canBeSubmitted || (!this.isConnectionAvaliable && !this.content.isConsole)) {
      return;
    }

    if (this.content.useCodeValidation) {
      this.submitValidation.emit({
        action: this.content.action, validationCode: this.validationCode
      });
    } else {
      this.submitRequest.emit({ action: this.content.action });
    }
  }

  // TODO: refactoring in setSubmitAvailability
  isSubmitAvailable() {
    if (this.content.useCodeValidation) {
      this.canBeSubmitted = this.areChecksOk && this.validationCode !== '';
    } else {
      this.canBeSubmitted = this.areChecksOk;
    }
  }

  checkboxChange() {
    this.areChecksOk = this.content.checks.length === this.checksStatus
      .filter(checkbox => checkbox).length;

    this.isSubmitAvailable();
  }

  ngOnChanges() {
    this._init();
  }

  ngOnInit() {
    this._init();
    this.socketHandler.connect$
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.isConnectionAvaliable = true;
        this.chRef.detectChanges();
        Observable.timer(0, 100).take(1).subscribe((tick) => {
          this.chRef.detectChanges();
          this.chRef.reattach();
        });
      });

    this.socketHandler.disconnect$
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.isConnectionAvaliable = false;
        this.chRef.detectChanges();
        Observable.timer(0, 100).take(1).subscribe((tick) => {
          this.chRef.detectChanges();
          this.chRef.reattach();
        });
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
  }
}



// WEBPACK FOOTER //
// ./src/app/components/cs-modal/cs-modal.component.ts